import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import TeamsService from 'api/services/TeamsService';
import { RootState } from 'store/store';
import { TStatisticITem, TTeamAgentsData, TTeamData } from 'store/types';

export const fetchTeams = createAsyncThunk<any, void, { state: RootState }>('fetchTeams', async (_, { getState }) => {
    const companyId = getState().userState.me.data.company.id;
    const response = await TeamsService.teams(companyId);
    return response.data;
});

export const createTeam = createAsyncThunk<any, { title: string }, { state: RootState }>(
    'createTeam',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await TeamsService.createTeam({ companyId, title: params.title });
        return response.data;
    },
);

export const updateTeam = createAsyncThunk<any, { title: string; id: string }, { state: RootState }>(
    'updateTeam',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await TeamsService.updateTeam({ companyId, ...params });
        return response.data;
    },
);

export const deleteTeam = createAsyncThunk<any, { id: string }, { state: RootState }>(
    'deleteTeam',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await TeamsService.deleteTeam({ companyId, ...params });
        return response.data;
    },
);

export const fetchSingleTeam = createAsyncThunk<any, { id: string }, { state: RootState }>(
    'fetchSingleTeam',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await TeamsService.team({ companyId, ...params });
        return response.data;
    },
);

export const fetchSingleTeamAgents = createAsyncThunk<any, { id: string }, { state: RootState }>(
    'fetchSingleTeamAgents',
    async (params, { getState }) => {
        const companyId = getState().userState.me.data.company.id;
        const response = await TeamsService.teamAgents({ companyId, ...params });
        return response.data;
    },
);

type TTeamsState = {
    teams: {
        data: [] | Array<TTeamData>;
        isLoading: boolean;
    };
    team: {
        data: TTeamData;
        isLoading: boolean;
    };
    teamAgents: {
        data: TTeamAgentsData;
        isLoading: boolean;
    };
    agentStatistic: {
        data:
            | {}
            | {
                  request: {
                      start: Date;
                      end: Date;
                  };
                  user: {
                      email: string;
                      fullName: string;
                  };
                  computed: {
                      calls: number;
                      earlyHangUps: number;
                      sms: number;
                      pickUps: number;
                      convos: number;
                      offers: number;
                      dq: number;
                      sets: number;
                      triage: number;
                      workTime: string;
                  };
                  timeframes: {};
              };
        isLoading: boolean;
    };
    agentStatisticByTimeframe: {
        data:
            | {}
            | {
                  request: {
                      start: Date;
                      end: Date;
                  };
                  user: {
                      firstName: string;
                      lastName: string;
                      email: string;
                  };
                  calculated: {
                      calls: number;
                      convos: number;
                      pickUps: number;
                      sms: number;
                      earlyHangUps: number;
                  };
                  activities: {
                      calls: Array<TStatisticITem>;
                      convos: [];
                      pickUps: [];
                      sms: Array<TStatisticITem>;
                      earlyHangUps: Array<TStatisticITem>;
                  };
              };
        isLoading: boolean;
    };
};

const initialState: TTeamsState = {
    teams: {
        data: [],
        isLoading: false,
    },
    team: {
        data: {} as TTeamData,
        isLoading: false,
    },
    teamAgents: {
        data: {} as TTeamAgentsData,
        isLoading: false,
    },
    agentStatistic: {
        data: {},
        isLoading: false,
    },
    agentStatisticByTimeframe: {
        data: {},
        isLoading: false,
    },
};

export const teamsSlice = createSlice({
    name: 'companies',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchTeams.pending, state => {
                state.teams.isLoading = true;
            })
            .addCase(fetchTeams.fulfilled, (state, action) => {
                state.teams.data = action.payload.teams;
                state.teams.isLoading = false;
            });
        builder
            .addCase(fetchSingleTeam.pending, state => {
                state.team.isLoading = true;
            })
            .addCase(fetchSingleTeam.fulfilled, (state, action) => {
                state.team.data = action.payload;
                state.team.isLoading = false;
            });
        builder
            .addCase(fetchSingleTeamAgents.pending, state => {
                state.teamAgents.isLoading = true;
            })
            .addCase(fetchSingleTeamAgents.fulfilled, (state, action) => {
                state.teamAgents.data = action.payload;
                state.teamAgents.isLoading = false;
            });
    },
});

export const selectTeams = createSelector([(state: RootState) => state.teamsState.teams], teams =>
    teams.data.map(item => ({
        id: item.id,
        title: item.title,
        members: item.Agents,
        variables: item.Variables,
        companyId: item.companyId,
    })),
);

export const selectTeamsLength = createSelector(
    [(state: RootState) => state.teamsState.teams],
    data => data.data.length,
);

export const selectTeamsIsLoading = createSelector(
    [(state: RootState) => state.teamsState.teams],
    data => data.isLoading,
);

export const selectSingleTeam = createSelector([(state: RootState) => state.teamsState.team], team => ({
    ...team.data,
    variables: team.data.Variables,
}));

export const selectSingleTeamsIsLoading = createSelector(
    [(state: RootState) => state.teamsState.team],
    data => data.isLoading,
);

export const selectSingleTeamsAgents = createSelector(
    [(state: RootState) => state.teamsState.teamAgents],
    agents => agents.data,
);

export const selectSingleTeamsAgentsIsLoading = createSelector(
    [(state: RootState) => state.teamsState.teamAgents],
    data => data.isLoading,
);

// export const selectAgentStatistic = createSelector(
//     [(state: RootState) => state.companiesState.agentStatistic],
//     data => data.data,
// );
// export const selectAgentStatisticIsLoading = createSelector(
//     [(state: RootState) => state.companiesState.agentStatistic],
//     data => data.isLoading,
// );

// export const selectAgentStatisticByTimeframe = createSelector(
//     [(state: RootState) => state.companiesState.agentStatisticByTimeframe],
//     data => data.data,
// );
// export const selectAgentStatisticByTimeframeIsLoading = createSelector(
//     [(state: RootState) => state.companiesState.agentStatisticByTimeframe],
//     data => data.isLoading,
// );
export default teamsSlice.reducer;
